import { atom, useAtom, useAtomValue } from "jotai";
import { DateTime, Duration } from "luxon";
import { useCallback, useEffect } from "react";
import { CartDto } from "@rinastyling/common";
import { BuyingShitService } from "../services/BuyingShitService";
import { StoreService } from "../services/StoreService";
import { handleFetchError } from "../utils/clientErrorHandeling";
import { userAtom } from "./userAtom";

export const cartAtom = atom<CartDto | null>(null);
export const cartTimerAtom = atom<string>("");

const useCart = () => {
  const [cart, setCart] = useAtom(cartAtom);
  const [timeLeft, setTimeLeft] = useAtom(cartTimerAtom);

  const user = useAtomValue(userAtom);

  useEffect(() => {
    if (!cart || !cart.items.length) {
      setTimeLeft("");
      return;
    }
    const lastEditedTime = DateTime.fromISO(cart.lastEditedTime);
    const cartExpiryTime = lastEditedTime.plus({ minutes: 4 });
    const updateTimer = () => {
      const now = DateTime.now();
      const diff = cartExpiryTime.diff(now);
      const diffInMinutes = diff.as("minutes");

      if (diffInMinutes <= 0) {
        setTimeLeft("");
        setCart(null);
      } else {
        const remaining = Duration.fromObject({
          minutes: diffInMinutes
        }).toFormat("mm:ss");
        setTimeLeft(remaining);
      }
    };
    updateTimer(); // initial call
    const timer = setInterval(updateTimer, 1000); // update every second

    return () => clearInterval(timer);
  }, [cart, setCart, setTimeLeft]);

  const getCart = useCallback(async () => {
    if (!user) return;
    try {
      const res = await new StoreService().getUsersCart();

      if (res.cart) {
        setCart(res.cart);
        return res.cart;
      }
    } catch (error) {
      console.error(error);
    }
  }, [setCart, user]);

  useEffect(() => {
    getCart();
  }, [getCart]);

  const addToCart = useCallback(
    async ({ itemId }: Parameters<BuyingShitService["AddItemToCart"]>[0]) => {
      try {
        const res = await new BuyingShitService().AddItemToCart({
          itemId
        });
        setCart(res.cart);
        return res.cart;
      } catch (error) {
        handleFetchError(error);
        return null;
      }
    },
    [setCart]
  );
  const removeItemFromCart = useCallback(
    async ({
      itemId
    }: Parameters<BuyingShitService["removeItemFromCart"]>[0]) => {
      try {
        const res = await new BuyingShitService().removeItemFromCart({
          itemId
        });

        setCart(res.cart);
        console.log(res.cart);

        return res.cart;
      } catch (error) {
        handleFetchError(error);
        return null;
      }
    },
    [setCart]
  );

  return { addToCart, cart, timeLeft, removeItemFromCart };
};

export default useCart;
